// angular
import {Injectable, inject, signal} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {Router, ActivatedRoute, Params} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {NgxSpinnerService} from "ngx-spinner";
// material angular
import {MatDialog} from "@angular/material/dialog";
// components
import {AssignTicketDialogComponent} from "@app/core/components/dialog/assign-ticket-dialog/assign-ticket-dialog.component";
// services
import {AppToastService} from "@app/shared/services/app-toast.service";
import {RouteService} from "@app/shared/services/route.service";
// types
import {ITableConfig} from "@app/core/components/table/table-config.type";
import {WithPagination} from "@app/shared/types/with-pagination.type";
import {
  ITicketListItem,
  ITicketDetail,
  ITicketMessage,
  ITicketReplyRequest,
  ITicketReplyResponse,
  IAssignTicketRequest,
  IAssignTicketResponse,
  IUpdateTicketStatusRequest,
  IUpdateTicketStatusResponse,
  TicketStatusEnum,
  TicketPriorityEnum,
} from "../types/ticket-management.type";
// utils
import {convertIsoToJalaali} from "@app/shared/utils/convert-iso-to-jalaali";
import {
  getTableAddressValue,
  getTableCoordinatesValue,
} from "@app/shared/utils/format-location-coordinates.util";

const prefix = "tickets";

@Injectable()
export class TicketManagementService {
  public ticketsListData = signal<WithPagination<ITicketListItem> | null>(null);
  public isTicketsListLoading = signal<boolean>(false);
  public ticketDetailsData = signal<ITicketDetail | null>(null);
  public isTicketDetailsLoading = signal<boolean>(false);
  public ticketMessagesData = signal<WithPagination<ITicketMessage> | null>(
    null
  );
  public isTicketMessagesLoading = signal<boolean>(false);

  // dependencies
  private _httpClient = inject(HttpClient);
  private _translate = inject(TranslateService);
  private _toast = inject(AppToastService);
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _spinner = inject(NgxSpinnerService);
  private _routeService = inject(RouteService);
  private _dialog = inject(MatDialog);

  constructor() {}

  public isTicketDataValid(): boolean {
    const ticketData = this.ticketDetailsData();
    return !!ticketData && !this.isTicketDetailsLoading();
  }

  // API calls
  public getAllTickets(params?: Params) {
    return this._httpClient.get<WithPagination<ITicketListItem>>(
      prefix.concat(this._routeService.getUrlQueryString()),
      {params}
    );
  }

  public getTicketById(id: string) {
    return this._httpClient.get<ITicketDetail>(`${prefix}/${id}`);
  }

  public getTicketMessages(id: string, params?: Params) {
    return this._httpClient.get<WithPagination<ITicketMessage>>(
      `${prefix}/${id}/messages`,
      {params}
    );
  }

  public replyToTicket(id: string, data: ITicketReplyRequest) {
    return this._httpClient.post<ITicketReplyResponse>(
      `${prefix}/${id}/reply`,
      data
    );
  }

  public assignTicket(id: string, data: IAssignTicketRequest) {
    return this._httpClient.patch<IAssignTicketResponse>(
      `${prefix}/${id}/assign`,
      data
    );
  }

  public updateTicketStatus(id: string, data: IUpdateTicketStatusRequest) {
    return this._httpClient.patch<IUpdateTicketStatusResponse>(
      `${prefix}/${id}/status`,
      data
    );
  }

  // Table configuration
  public getTicketsTableConfig(
    tickets: WithPagination<ITicketListItem>
  ): ITableConfig {
    return {
      name: "tickets",
      displayedColumns: [
        "subject",
        "customer",
        "address",
        "coordinates",
        "creator",
        "status",
        "priority",
        "assignedTo",
        "created_at",
        "actions",
      ],
      originalSource: JSON.parse(JSON.stringify(tickets.data)),
      dataSource: tickets.data.map((item) => ({
        ...item,
        customer: item.customer?.title,
        address: getTableAddressValue(item),
        coordinates: getTableCoordinatesValue(item),
        creator: item.creator_person?.profile
          ? `${item.creator_person.profile.first_name} ${item.creator_person.profile.last_name}`
          : "-",
        assignedTo: item.assigned_to?.profile
          ? `${item.assigned_to.profile.first_name} ${item.assigned_to.profile.last_name}`
          : "-",
        created_at: convertIsoToJalaali(item.created_at),
        status: this._getStatusTranslation(item.status),
        priority: this._getPriorityTranslation(item.priority),
      })),
      pagination: {
        count: tickets.count,
        page: this._activatedRoute.snapshot.queryParams["page"],
        pageSize: this._activatedRoute.snapshot.queryParams["page-size"],
      },
      minWidth: "1400px",
      columnsWidths: [
        "16%",
        "11%",
        "16%",
        "12%",
        "11%",
        "10%",
        "9%",
        "11%",
        "9%",
        "10%",
      ],
      columnsCssClasses: {
        status: (ticket) => this._getStatusCssClass(ticket),
        priority: (ticket) => this._getPriorityCssClass(ticket),
      },
      columnsIcons: {
        status: (ticket) => this._getStatusIcon(ticket),
        priority: (ticket) => this._getPriorityIcon(ticket),
      },
      actions: [
        {
          icon: "visibility",
          handler: (ticket) => this.navigateToTicketDetails(ticket.id),
          cssClass: "!text-info",
        },
        {
          icon: "person_add",
          handler: (ticket) => this.openAssignTicketDialog(ticket.id),
          cssClass: "!text-success",
        },
      ],
    };
  }

  // Translations
  private _getStatusTranslation(status: TicketStatusEnum) {
    return this._translate.instant("enums.TicketStatusEnum.".concat(status));
  }

  private _getPriorityTranslation(priority: TicketPriorityEnum) {
    return this._translate.instant(
      "enums.TicketPriorityEnum.".concat(priority)
    );
  }

  // Status styling helpers
  private _getStatusCssClass(ticket: ITicketListItem): string {
    switch (ticket.status) {
      case TicketStatusEnum.OPEN:
      case TicketStatusEnum.REOPENED:
        return "!text-info";
      case TicketStatusEnum.WAITING_CUSTOMER:
      case TicketStatusEnum.WAITING_SUPPORT:
        return "!text-warning";
      case TicketStatusEnum.RESOLVED:
        return "!text-success";
      case TicketStatusEnum.CLOSED:
        return "!text-secondary-main";
      default:
        return "";
    }
  }

  private _getPriorityCssClass(ticket: ITicketListItem): string {
    switch (ticket.priority) {
      case TicketPriorityEnum.LOW:
        return "!text-secondary-main";
      case TicketPriorityEnum.MEDIUM:
        return "!text-info";
      case TicketPriorityEnum.HIGH:
        return "!text-warning";
      case TicketPriorityEnum.URGENT:
        return "!text-danger";
      default:
        return "";
    }
  }

  private _getStatusIcon(ticket: ITicketListItem): string {
    switch (ticket.status) {
      case TicketStatusEnum.OPEN:
        return "fiber_new";
      case TicketStatusEnum.REOPENED:
        return "refresh";
      case TicketStatusEnum.WAITING_CUSTOMER:
        return "schedule";
      case TicketStatusEnum.WAITING_SUPPORT:
        return "support_agent";
      case TicketStatusEnum.RESOLVED:
        return "check_circle";
      case TicketStatusEnum.CLOSED:
        return "lock";
      default:
        return "help";
    }
  }

  private _getPriorityIcon(ticket: ITicketListItem): string {
    switch (ticket.priority) {
      case TicketPriorityEnum.LOW:
        return "arrow_downward";
      case TicketPriorityEnum.MEDIUM:
        return "remove";
      case TicketPriorityEnum.HIGH:
        return "arrow_upward";
      case TicketPriorityEnum.URGENT:
        return "priority_high";
      default:
        return "remove";
    }
  }

  // Navigation
  public navigateToTicketDetails(id: string) {
    this._router.navigate([`/ticket-management/${id}`]);
  }

  public navigateToTicketMessages(id: string) {
    this._router.navigate([`/ticket-management/${id}/messages`]);
  }

  public openAssignTicketDialog(ticketId: string) {
    const dialogRef = this._dialog.open(AssignTicketDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data: {ticketId},
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getTicketsList();
      }
    });
  }

  // Data fetching with signals
  public getTicketsList() {
    this.isTicketsListLoading.set(true);
    this.getAllTickets().subscribe({
      next: (res) => {
        this.ticketsListData.set(res);
        this.isTicketsListLoading.set(false);
      },
      error: () => {
        this.ticketsListData.set(null);
        this.isTicketsListLoading.set(false);
      },
      complete: () => {
        this.isTicketsListLoading.set(false);
      },
    });
  }

  public getTicketDetails(id: string) {
    this.isTicketDetailsLoading.set(true);
    this.getTicketById(id).subscribe({
      next: (res) => {
        this.ticketDetailsData.set(res);
        this.isTicketDetailsLoading.set(false);
      },
      error: () => {
        this.ticketDetailsData.set(null);
        this.isTicketDetailsLoading.set(false);
      },
      complete: () => {
        this.isTicketDetailsLoading.set(false);
      },
    });
  }

  public getMessages(id: string, params?: Params) {
    this.isTicketMessagesLoading.set(true);
    this.getTicketMessages(id, params).subscribe({
      next: (res) => {
        this.ticketMessagesData.set(res);
        this.isTicketMessagesLoading.set(false);
      },
      error: () => {
        this.ticketMessagesData.set(null);
        this.isTicketMessagesLoading.set(false);
      },
      complete: () => {
        this.isTicketMessagesLoading.set(false);
      },
    });
  }

  // Actions
  public sendReply(id: string, data: ITicketReplyRequest) {
    this._spinner.show();
    this.replyToTicket(id, data).subscribe({
      next: () => {
        this._toast.success("ticketManagement.replySuccess");
        this.getMessages(id);
        this.getTicketDetails(id);
      },
      error: () => this._spinner.hide(),
      complete: () => this._spinner.hide(),
    });
  }

  public assignToEmployee(id: string, data: IAssignTicketRequest) {
    this._spinner.show();
    this.assignTicket(id, data).subscribe({
      next: () => {
        this._toast.success("ticketManagement.assignSuccess");
        this.getTicketDetails(id);
      },
      error: () => this._spinner.hide(),
      complete: () => this._spinner.hide(),
    });
  }

  public changeStatus(id: string, data: IUpdateTicketStatusRequest) {
    this._spinner.show();
    this.updateTicketStatus(id, data).subscribe({
      next: () => {
        this._toast.success("ticketManagement.statusUpdateSuccess");
        this.getTicketDetails(id);
      },
      error: () => this._spinner.hide(),
      complete: () => this._spinner.hide(),
    });
  }
}
